import { useEffect, useMemo, useState } from 'react';
import {
  companionDirectives,
  companionHeld,
  companionHeldDecide,
  companionNextStep,
  companionPastThread,
  companionPastThreads,
  companionRoster,
  companionSummary,
  companionToday,
  companionTranscriptOpen,
  type CompanionDirective,
  type CompanionHeldResponse,
  type CompanionRosterRow,
  type CompanionSummary,
  type CompanionToday,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { facilitiesParam, usePickedIds } from '../../session/FacilityScope';
import { ScopeEmpty, nothingFrom } from '../../ui/ScopeEmpty';
import type { QueryState } from '../../useQuery';
import { A, Sample, firstName, monthYear, minutesSince, shortAge, st, useApi } from './ui';
import { liveView, useCompanionLive } from './live';
import { sendChat } from './chat';
import { tierLabel } from '../residents/common';
import { useCustomerText } from '../../ui/buildLabels';

// The Operator, Facility Admin and Care Staff Atlas: "Conversations" (design T525-707,
// coIsOpChat). Every row and figure comes from a route; a route that has not answered, or
// is not served yet, leaves its block in the design's empty state.

export type Lens = 'op' | 'admin' | 'care';

const COMP_COLORS = ['#2F5C97', '#1F6B52', '#44505E', '#A85F52', '#377E96', '#857446'];
const MOOD_RANK: Record<string, number> = { anxious: 0, tired: 1, restless: 2, settled: 3, bright: 4 };
const VC2: Record<string, string> = { ALLOW: '#1F6B52', FLAG: '#4A5866', ESCALATE: '#5B6B7C', HALT: '#B3261E', BLOCK: '#B3261E' };
const REASONS = ['Care follow-up', 'Safety concern', 'Resident asked', 'Family request'];

/** The design's roster order: flags first, then the more worrying mood, then the name. */
export function rosterOrder(rows: CompanionRosterRow[]): CompanionRosterRow[] {
  const rank = (m: string | null) => (m && m in MOOD_RANK ? MOOD_RANK[m] : 5);
  return rows.slice().sort((a, b) => b.flags - a.flags || rank(a.mood) - rank(b.mood) || a.displayName.localeCompare(b.displayName));
}

/** "4 need you today", "38 of 44 reached": the design's hero sentence and figures, from the summary. */
/** Initials of a name as the list shows them ("Margaret Doyle" -> "MD"). */
const initialsOfName = (s: string) => s.split(/[\s.]+/).filter(Boolean).map((w) => w[0]).join('').toUpperCase();

/**
 * The roster rows a search keeps: a match on the name or display name the server sent, the
 * room, or the initials of the sent name. Client side, on the loaded list only.
 */
export function rosterMatches(rows: CompanionRosterRow[], query: string): CompanionRosterRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  const bare = q.replace(/^(room|rm)\s*/, '');
  const ini = q.replace(/[\s.]/g, '').toUpperCase();
  return rows.filter((r) => {
    if (r.name.toLowerCase().includes(q) || r.displayName.toLowerCase().includes(q)) return true;
    if (r.room && String(r.room).toLowerCase().includes(bare)) return true;
    return !!ini && /^[A-Z]+$/.test(ini) && initialsOfName(r.name).startsWith(ini);
  });
}

export function heroFor(lens: Lens, s: CompanionSummary) {
  const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
  if (lens === 'care') {
    const need = s.needYou ?? 0;
    return {
      line: `Atlas has something new on ${need} of the ${plural(need, 'resident', 'residents')} on this floor.`,
      stats: [
        [String(need), 'need you today', '#B3261E'],
        [String(s.enrolled), 'on this floor', '#22272E'],
        [String(s.goneQuiet), 'gone quiet', '#4A5866'],
        [String(s.instructionsInForce), 'instructions to follow', '#0F6B63'],
      ],
    };
  }
  if (lens === 'admin') {
    return {
      line: `Atlas reached ${s.spokeToday} of your ${s.enrolled} residents today and refused ${s.refusedToday} of them.`,
      stats: [
        [String(s.spokeToday), `of ${s.enrolled} reached`, '#22272E'],
        [String(s.goneQuiet), 'gone quiet', '#B3261E'],
        [String(s.refusedToday), 'refused', '#B3261E'],
        [String(s.instructionsInForce), 'instructions in force', '#0F6B63'],
      ],
    };
  }
  return {
    line: `${s.spokeToday} ${plural(s.spokeToday, 'resident', 'residents')} talked with Atlas today. ${s.refusedToday === 0 ? 'It declined no requests.' : `It declined ${s.refusedToday} ${plural(s.refusedToday, 'request', 'requests')} and handed ${s.refusedToday === 2 ? 'both' : s.refusedToday === 1 ? 'it' : 'each'} to a nurse.`}`,
    stats: [
      [String(s.spokeToday), 'spoke today', '#22272E'],
      [String(s.goneQuiet), 'gone quiet', '#B3261E'],
      [String(s.refusedToday), 'refused', '#B3261E'],
      [s.medianCheckMs === null ? '–' : `${s.medianCheckMs}ms`, 'typical check', '#1F6B52'],
    ],
  };
}

function Hero({ lens, q }: { lens: Lens; q: QueryState<CompanionSummary> }) {
  const plain = useCustomerText();
  const h = q.data ? heroFor(lens, q.data) : null;
  return (
    <div style={st('flex:0 0 auto;display:flex;align-items:baseline;gap:10px 22px;flex-wrap:wrap;row-gap:5px;min-width:0;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.14);')}>
      <span style={st('flex:1 1 300px;min-width:0;font-size:18px;font-weight:700;letter-spacing:-.022em;line-height:1.25;text-wrap:pretty;color:#22272E;')}>
        {h ? h.line : q.loading ? '' : plain('The Atlas summary is not served yet.')}
        {q.data && <> <Sample real={q.data.real} figure="Atlas summary" /></>}
      </span>
      {(h?.stats ?? []).map(([v, k, color]) => (
        <span key={k} style={st('flex:0 0 auto;display:inline-flex;align-items:baseline;gap:5px;white-space:nowrap;')}>
          <span style={st(`font-size:16px;font-weight:700;letter-spacing:-.025em;line-height:1;font-variant-numeric:tabular-nums;color:${color};`)}>{v}</span>
          <span style={st('font-size:14px;color:#54606C;')}>{k}</span>
        </span>
      ))}
    </div>
  );
}

function Held({ lens, q }: { lens: Lens; q: QueryState<CompanionHeldResponse> }) {
  const { client } = useSession();
  const { role } = useConsoleRole();
  const [decided, setDecided] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  if (lens === 'care' || !q.data) return null;
  const open = q.data.items.filter((h) => h.kind === 'companion' && !decided[h.id]);
  const decide = async (id: string, decision: 'allow' | 'refuse') => {
    setErr(null);
    try {
      await companionHeldDecide(client, { params: { id }, body: { decision } });
      setDecided((d) => ({ ...d, [id]: decision }));
      q.reload();
    } catch (e) {
      setErr((e as Error).message || 'That decision was not recorded.');
    }
  };
  if (!open.length) {
    return (
      <span style={st('flex:0 0 auto;display:block;font-size:14px;font-weight:600;color:#12503C;line-height:1.4;padding-bottom:8px;margin-bottom:8px;border-bottom:1px solid rgba(34,39,46,.14);text-wrap:pretty;')}>
        Nothing is waiting on a person. Every held turn today has been decided.
      </span>
    );
  }
  const now = Date.now();
  return (
    <span style={st('flex:0 0 auto;display:block;min-width:0;padding-bottom:8px;margin-bottom:8px;border-bottom:1px solid rgba(34,39,46,.14);')}>
      <span style={st('display:flex;align-items:baseline;gap:7px;padding-bottom:5px;min-width:0;')}>
        <span style={st('flex:1 1 0;min-width:0;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#B3261E;display:inline-flex;align-items:center;gap:7px;')}>
          <span style={st('width:7px;height:7px;border-radius:50%;background:#B3261E;animation:coPulse 1.8s ease-out infinite;')} />
          Waiting on you
        </span>
        <span style={st('flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:#B3261E;')}>{String(open.length)}</span>
      </span>
      <span style={st('display:block;min-width:0;')}>
        {open.map((h, i) => {
          const age = minutesSince(h.surfacedAt, now) ?? 0;
          const who = h.resident?.name ?? [h.resident?.initials, h.resident?.room].filter(Boolean).join(' · ');
          return (
            <span key={h.id} style={st(`display:block;padding:7px 9px;margin-bottom:4px;border-radius:6px;min-width:0;background:rgba(179,38,30,.06);animation:riseIn .34s cubic-bezier(.22,.61,.36,1);animation-delay:${i * 55}ms;`)}>
              <span style={st('display:flex;align-items:baseline;gap:6px;min-width:0;')}>
                <span style={st('flex:1 1 0;min-width:0;font-size:14px;font-weight:700;overflow-wrap:break-word;')}>{who || '–'}</span>
                <span style={st(`flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:${age > 20 ? '#B3261E' : age > 10 ? '#4A5866' : '#54606C'};`)}>{`${age}m`}</span>
              </span>
              <span style={st('display:block;font-size:14px;color:#3C454F;line-height:1.35;margin-top:1px;text-wrap:pretty;')}>{h.summary ?? '–'}</span>
              <span style={st('display:block;font-size:14px;color:#54606C;margin-top:1px;')}>{h.gate}</span>
              {lens === 'op' && <span style={st('display:block;font-size:14px;color:#54606C;margin-top:4px;')}>{`Read-only as ${tierLabel(role)} · staff decide`}</span>}
              {lens !== 'op' && (
                <span style={st('display:flex;gap:5px;margin-top:5px;flex-wrap:wrap;')}>
                  <A write onClick={() => void decide(h.id, 'allow')} s="display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#1F6B52;">
                    Let it answer
                  </A>
                  <A write onClick={() => void decide(h.id, 'refuse')} s="display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#B3261E;background:rgba(179,38,30,.12);">
                    Keep blocked
                  </A>
                </span>
              )}
            </span>
          );
        })}
      </span>
      {err && <span role="alert" style={st('display:block;font-size:14px;color:#B3261E;margin-top:4px;')}>{err}</span>}
    </span>
  );
}

/** The design's line under "Refused today", counted from what was refused. */
export function refusedNote(n: number | null): string {
  if (n === null) return '';
  if (n === 0) return 'Nothing was refused today.';
  const tail = ' Refusals are the product working, not the product failing.';
  if (n === 1) return `That refusal is in the Truth Ledger with the layer that caught it.${tail}`;
  return `${n === 2 ? 'Both refusals are' : `All ${n} refusals are`} in the Truth Ledger with the layer that caught them.${tail}`;
}

/** Days until an instruction stops steering. */
export function daysLeft(iso: string | null, now = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : Math.ceil((t - now) / 86400000);
}

export function RichChat({ lens, selected, onSelect, onGoMemory }: { lens: Lens; selected: string | null; onSelect: (id: string) => void; onGoMemory: () => void }) {
  const plain = useCustomerText();
  const { client, session } = useSession();
  const isOp = lens === 'op';
  // The facility switcher (V6): Super Admin and Operator read the picked buildings' residents.
  const facilities = facilitiesParam(usePickedIds());
  const pickQ = facilities ? { facilities } : undefined;
  const summary = useApi<CompanionSummary>((c, signal) => companionSummary(c, { query: pickQ, signal }), [facilities]);
  const held = useApi((c, signal) => companionHeld(c, { query: pickQ, signal }), [facilities], lens !== 'care');
  // Polled until the companion.turn feed exists, so "in a conversation now" stays current.
  const roster = useApi((c, signal) => companionRoster(c, { query: pickQ, signal }), [facilities], true, 30_000);
  const live = liveView(useCompanionLive());

  const rows = useMemo(() => (roster.data ? rosterOrder(roster.data.residents) : []), [roster.data]);
  const colorOf = useMemo(() => new Map(rows.map((r, i) => [r.residentId, COMP_COLORS[i % COMP_COLORS.length]])), [rows]);
  const selId = selected ?? roster.data?.focusResidentId ?? rows[0]?.residentId ?? null;
  const sel = rows.find((r) => r.residentId === selId) ?? null;
  // Search the loaded list only, on the names the server already chose to send: a masked row
  // carries initials, so a search can never find (or show) a name the server held back.
  const [find, setFind] = useState('');
  const shown = useMemo(() => rosterMatches(rows, find), [rows, find]);
  // Two residents with the same first name are told apart by room, else last initial (wrong-resident risk).
  const labelOf = useMemo(() => rosterLabels(rows), [rows]);

  const today = useApi<CompanionToday>((c, signal) => companionToday(c, { params: { id: selId! }, signal }), [selId], !!selId);
  const directives = useApi((c, signal) => companionDirectives(c, { params: { id: selId! }, signal }), [selId], !!selId);
  // The channel chips start on voice and keep their choice from one resident to the next,
  // as the design's console-level `device` state does.
  const [device, setDevice] = useState<string>('voice');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    setNote(null);
  }, [selId]);

  const t = today.data;
  const first = sel ? firstName(sel.displayName) : '';
  const opened = t?.opened ?? null;
  const locked = !!t && t.turnCount > 0 && (isOp || !opened);
  const unlocked = !!t && !locked && t.turnCount > 0;
  const devs = sel?.devices.length ? ['voice', 'phone', 'tablet', 'speaker'] : [];
  const curDevice = device;

  const openTranscript = async (reason: string) => {
    if (!selId) return;
    setNote(null);
    try {
      const d = await companionTranscriptOpen(client, { params: { id: selId }, body: { reason } });
      today.setData(() => d);
    } catch (e) {
      setNote((e as Error).message || 'The transcript could not be opened.');
    }
  };
  const closeTranscript = () => today.setData((p) => (p ? { ...p, opened: null, turns: p.turns.map((x) => ({ ...x, text: null })) } : p));
  const toggleNext = async () => {
    if (!selId || !t?.next) return;
    try {
      const r = await companionNextStep(client, { params: { id: selId }, body: { queued: !t.next.queued } });
      today.setData((p) => (p && p.next ? { ...p, next: { ...p.next, queued: r.queued } } : p));
    } catch (e) {
      setNote((e as Error).message || 'The step was not added.');
    }
  };
  const send = async () => {
    const v = draft.trim();
    if (!v || !selId || !session?.user.organizationId) return;
    setBusy(true);
    setNote(null);
    try {
      await sendChat(client, { message: v, userId: selId, organizationId: session.user.organizationId });
      setDraft('');
      today.reload();
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const nextDone = !!t?.next?.queued;
  const liveDirectives: CompanionDirective[] = (directives.data?.directives ?? []).filter((d) => d.effectiveActive);

  // A pick with no resident talking to Atlas in it (design geoLayer, Atlas).
  if (facilities && roster.data && !roster.data.residents.length) {
    return <ScopeEmpty title={nothingFrom} body="No resident in the buildings in this view talks with Atlas yet, so there are no conversations or notes to read." />;
  }

  return (
    <div style={st('flex:1 1 auto;display:flex;flex-direction:column;min-height:0;overflow:hidden;min-width:0;gap:10px;')}>
      <Hero lens={lens} q={summary} />

      <div style={st('flex:1 1 auto;display:flex;flex-wrap:nowrap;gap:0 20px;min-height:0;min-width:0;overflow-x:auto;overflow-y:hidden;')}>
        <div style={st('flex:1 1 190px;min-width:190px;display:flex;flex-direction:column;min-height:0;overflow-y:auto;')}>
          <Held lens={lens} q={held} />
          <span style={st('flex:0 0 auto;display:flex;flex-wrap:wrap;align-items:baseline;gap:2px 8px;padding-bottom:7px;border-bottom:1px solid rgba(34,39,46,.1);margin-bottom:6px;')}>
            <span style={st('flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;')}>Recent</span>
            {roster.data && <Sample real={roster.data.real} figure="Atlas roster" />}
            <span title={live.title} style={st(live.style)}>
              <span style={st(live.dot)} />
              {live.label}
            </span>
            {/* Wraps to its own line when the column is narrow, never one letter per line. */}
            <span data-testid="recent-active" style={st('flex:1 1 auto;min-width:max-content;font-size:14px;color:#54606C;text-align:right;white-space:nowrap;')}>
              {roster.data ? `${rows.filter((r) => r.inConversation).length} in a conversation now` : ''}
            </span>
          </span>
          {rows.length > 0 && (
            <input
              type="search"
              value={find}
              onChange={(e) => setFind(e.target.value)}
              placeholder="Search by name, room or initials"
              aria-label="Search residents"
              data-testid="conv-search"
              style={st('flex:0 0 auto;height:36px;padding:0 10px;margin-bottom:6px;border:1px solid rgba(34,39,46,.2);border-radius:8px;font-family:Archivo,sans-serif;font-size:16px;background:#fff;color:#22272E;outline:none;min-width:0;box-sizing:border-box;')}
            />
          )}
          <div style={st('flex:1 1 auto;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:1px;')}>
            {rows.length > 0 && !shown.length && (
              <span role="status" style={st('font-size:14px;color:#54606C;line-height:1.45;padding:6px 2px;text-wrap:pretty;')}>
                No resident matches “{find.trim()}”.
              </span>
            )}
            {!roster.data && !roster.loading && (
              <span style={st('font-size:14px;color:#54606C;line-height:1.45;padding:6px 2px;text-wrap:pretty;')}>
                {plain('No residents to list: the Atlas roster is not served yet.')}
              </span>
            )}
            {shown.map((c) => {
              const on = c.residentId === selId;
              const alerts = c.flags;
              return (
                <A
                  key={c.residentId}
                  onClick={() => onSelect(c.residentId)}
                  selected={on}
                  s={`display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;padding:9px 11px;border-radius:9px;text-decoration:none;color:#22272E;${on ? 'background:rgba(47,138,111,.10);border:1px solid rgba(47,138,111,.3);' : 'border:1px solid transparent;'}`}
                  hover="background:rgba(34,39,46,.04);"
                >
                  <span style={st('position:relative;display:inline-flex;flex:0 0 30px;')}>
                    <span style={st(`display:flex;align-items:center;justify-content:center;width:30px;height:30px;flex:0 0 30px;border-radius:50%;color:#fff;font-size:14px;font-weight:700;background:${colorOf.get(c.residentId)};`)}>
                      {c.displayName.slice(0, 2).toUpperCase()}
                    </span>
                    <span style={st(`position:absolute;left:-1px;bottom:-1px;width:8px;height:8px;border-radius:50%;background:${c.inConversation && live.label.endsWith('talking now') ? '#1F6B52' : '#7C8794'};border:2px solid #F5F1E8;box-sizing:border-box;`)} />
                  </span>
                  <span style={st('min-width:0;flex:1 1 0;')}>
                    <span style={st('display:flex;align-items:center;gap:6px;min-width:0;')}>
                      <span translate="no" style={st('flex:1 1 0;min-width:0;font-size:16px;font-weight:600;overflow-wrap:anywhere;')}>{labelOf.get(c.residentId) ?? c.displayName}</span>
                      <span
                        style={st(
                          alerts > 0
                            ? 'display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:5px;background:rgba(179,38,30,.1);color:#9E2019;font-size:14px;font-weight:700;flex:0 0 auto;box-sizing:border-box;white-space:nowrap;'
                            : 'display:none;',
                        )}
                      >
                        {alerts > 0 ? `${alerts}${alerts > 1 ? ' flags' : ' flag'}` : ''}
                      </span>
                    </span>
                    <span style={st('display:flex;align-items:center;gap:5px;min-width:0;')}>
                      <span style={st('flex:1 1 0;min-width:0;font-size:14px;color:#54606C;overflow-wrap:anywhere;')}>
                        {c.devices.length ? `on ${c.devices.join(' and ')}` : 'no device yet'}
                      </span>
                      <span style={st(`font-size:14px;flex:0 0 auto;color:${(MOOD_RANK[c.mood ?? ''] ?? 5) <= 1 ? '#5B6B7C' : '#54606C'};`)}>{c.mood ?? ''}</span>
                    </span>
                  </span>
                </A>
              );
            })}
          </div>
        </div>

        <div style={st('flex:3 1 320px;min-width:300px;display:flex;flex-direction:column;min-height:0;')}>
          <div style={st('flex:0 0 auto;display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding-bottom:9px;min-width:0;')}>
            {sel && (
              <span style={st(`display:flex;align-items:center;justify-content:center;width:38px;height:38px;flex:0 0 38px;border-radius:50%;color:#fff;font-size:16px;font-weight:700;background:${colorOf.get(sel.residentId)};`)}>
                {sel.displayName.slice(0, 2).toUpperCase()}
              </span>
            )}
            <span style={st('flex:1 1 140px;min-width:0;')}>
              <span style={st('display:block;font-size:17px;font-weight:700;letter-spacing:-.015em;')}>{sel?.displayName ?? ''}</span>
              <span style={st('display:block;font-size:14px;color:#54606C;overflow-wrap:break-word;')}>{sel ? `On Atlas since ${monthYear(sel.since)}` : ''}</span>
            </span>
            <span style={st('flex:0 0 auto;display:inline-flex;gap:2px;')}>
              {devs.map((d) => (
                <A
                  key={d}
                  onClick={() => setDevice(d)}
                  selected={curDevice === d}
                  s={`display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:7px;font-size:16px;letter-spacing:.01em;text-decoration:none;${curDevice === d ? 'color:#1F6B52;background:rgba(47,138,111,.14);font-weight:700;' : 'color:#54606C;'}`}
                >
                  {d}
                </A>
              ))}
            </span>
          </div>

          <div style={st('flex:1 1 auto;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:9px;padding-right:4px;')}>
            {t && t.turnCount === 0 && (
              <div style={st('font-size:16px;color:#54606C;line-height:1.5;max-width:56ch;text-wrap:pretty;')}>
                {`No conversation recorded with ${first} today. That is an empty transcript, not a quiet one, Atlas has not reached them.`}
              </div>
            )}
            {t?.next && (
              <div style={st('flex:0 0 auto;display:flex;align-items:center;gap:10px 14px;flex-wrap:wrap;max-width:72ch;padding:12px 14px;border-radius:10px;background:rgba(15,107,99,.08);min-width:0;')}>
                <span style={st('flex:1 1 260px;min-width:0;')}>
                  <span style={st('display:block;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#0F6B63;')}>Suggested next step</span>
                  <span style={st('display:block;font-size:16px;font-weight:600;line-height:1.45;color:#22272E;margin-top:3px;text-wrap:pretty;')}>{t.next.text}</span>
                </span>
                {!isOp && (
                  <A
                    write
                    onClick={() => void toggleNext()}
                    s={`flex:0 0 auto;display:inline-flex;align-items:center;min-height:36px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;${nextDone ? 'color:#12503C;background:rgba(31,107,82,.12);' : 'color:#FCFAF4;background:#0F6B63;'}`}
                  >
                    {nextDone ? (lens === 'care' ? 'On your round' : 'Sent to the floor') : lens === 'care' ? 'Add to my round' : 'Send to the floor'}
                  </A>
                )}
              </div>
            )}
            {locked && t && (
              <div style={st('display:flex;flex-direction:column;gap:10px;max-width:60ch;min-width:0;padding:4px 0;')}>
                <div style={st('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;')}>
                  Summary · <span>{`${t.turnCount} turns today`}</span>
                </div>
                <div style={st('display:grid;grid-template-columns:auto minmax(0,1fr);gap:6px 12px;font-size:16px;line-height:1.45;color:#22272E;')}>
                  <span style={st('font-weight:700;color:#54606C;')}>Heard</span>
                  <span style={st('text-wrap:pretty;')}>{t.summary?.heard ?? 'Conversation recorded today.'}</span>
                  <span style={st('font-weight:700;color:#54606C;')}>Did</span>
                  <span style={st('text-wrap:pretty;')}>{t.summary?.did ?? 'No action taken.'}</span>
                </div>
                <div style={st('font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
                  {isOp
                    ? 'You see what was said in summary. Full transcripts stay with each resident’s care team, and opening one takes a stated reason that is logged.'
                    : 'Residents have a right to talk without being overheard. Open the full transcript only with a reason. It is logged under your name.'}
                </div>
                {!isOp && (
                  <div style={st('display:flex;align-items:center;flex-wrap:wrap;gap:6px;')}>
                    <span style={st('font-size:14px;font-weight:700;color:#22272E;')}>Open transcript for</span>
                    {REASONS.map((r) => (
                      <A
                        key={r}
                        write
                        onClick={() => void openTranscript(r)}
                        s="display:inline-flex;align-items:center;height:30px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#22272E;border:1px solid rgba(34,39,46,.2);"
                        hover="background:rgba(34,39,46,.05);"
                      >
                        {r}
                      </A>
                    ))}
                  </div>
                )}
              </div>
            )}
            {unlocked && t && (
              <>
                <div style={st('display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:14px;color:#54606C;padding-bottom:2px;')}>
                  <span style={st('flex:1 1 auto;min-width:0;')}>{opened ? `Opened for ${opened.reason} at ${new Date(opened.at).toTimeString().slice(0, 5)} · logged as you` : ''}</span>
                  <A onClick={closeTranscript} s="flex:0 0 auto;display:inline-flex;align-items:center;min-height:32px;padding:0 10px;border-radius:7px;font-weight:700;color:#0F6B63;text-decoration:none;background:rgba(15,107,99,.08);">
                    Close transcript
                  </A>
                </div>
                {t.turns.map((x, i) => {
                  const mine = x.who === 'companion';
                  return (
                    <div key={i} style={st(`display:flex;flex-direction:column;align-items:${mine ? 'flex-start' : 'flex-end'};min-width:0;animation:riseIn .34s cubic-bezier(.22,.61,.36,1);animation-delay:${i * 55}ms;`)}>
                      <div style={st(`max-width:78%;padding:9px 13px;font-size:16px;line-height:1.45;text-wrap:pretty;border-radius:${mine ? '3px 13px 13px 13px;background:rgba(34,39,46,.06);color:#22272E;' : '13px 3px 13px 13px;background:#2F5C97;color:#FCFAF4;'}`)}>
                        {x.text ?? '–'}
                      </div>
                      <div style={st('display:flex;align-items:center;gap:6px;font-size:14px;color:#54606C;margin-top:3px;font-variant-numeric:tabular-nums;')}>
                        {/* the design's {{ t.meta }} is its own (possibly empty) flex item, so the gap applies */}
                        <span>{isOp ? `${x.layer} · ${x.latencyMs}ms · ` : ''}</span>
                        <span style={st(`display:inline-flex;align-items:center;height:15px;padding:0 6px;border-radius:3px;font-size:14px;font-weight:700;letter-spacing:.06em;color:${VC2[x.verdict]};background:${VC2[x.verdict]}1A;`)}>{x.verdict}</span>
                      </div>
                      {mine && x.steeredBy && (
                        <div style={st('font-size:14px;color:#0F6B63;font-weight:600;margin-top:2px;')}>
                          steered by your instruction: <span>{x.steeredBy}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Earlier conversations: restored from the old Flutter app; the same gate as today's words. */}
                {selId && <PastThreads residentId={selId} />}
              </>
            )}
            {note && <div role="alert" style={st('font-size:14px;color:#B3261E;line-height:1.45;')}>{note}</div>}
          </div>

          {!isOp && !locked && sel && (
            <div style={st('flex:0 0 auto;display:flex;align-items:center;flex-wrap:wrap;gap:8px 10px;padding-top:10px;margin-top:2px;border-top:1px solid rgba(34,39,46,.1);min-width:0;')}>
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void send();
                }}
                placeholder="Say something as Atlas"
                aria-label={`Say something to ${first} as Atlas`}
                style={st('box-sizing:border-box;flex:1 1 180px;min-width:180px;height:36px;padding:0 13px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;')}
              />
              <A write disabledReason={busy ? 'Sending' : null} onClick={() => void send()} s="flex:0 0 auto;display:inline-flex;align-items:center;height:36px;padding:0 15px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;">
                Send
              </A>
              <span style={st('flex:1 1 100%;min-width:0;font-size:14px;color:#54606C;')}>gate on every turn · sealed to the ledger</span>
            </div>
          )}
        </div>

        <div style={st('flex:1 1 220px;min-width:200px;display:flex;flex-direction:column;min-height:0;overflow-y:auto;gap:14px;')}>
          <div style={st('min-width:0;')}>
            <span style={st('display:block;font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;padding-bottom:6px;')}>What it is carrying</span>
            {t && t.carrying.length === 0 && (
              <span style={st('display:block;font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
                {`Atlas carries nothing about ${first} yet. No conversation has produced a fact it can source.`}
              </span>
            )}
            {(t?.carrying ?? []).slice(0, 5).map((f) => (
              <span key={f.name} style={st('display:flex;align-items:center;gap:8px;padding:3px 0;min-width:0;')}>
                <span style={st('flex:1 1 0;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;')}>{f.name}</span>
                <span style={st('flex:0 0 40px;height:5px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;')}>
                  <span style={st(`display:block;height:100%;width:${f.salience}%;border-radius:3px;transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) forwards;background:${f.salience >= 80 ? '#2F5C97' : f.salience >= 60 ? '#5B6B7C' : 'rgba(34,39,46,.3)'};`)} />
                </span>
                <span style={st('flex:0 0 auto;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{shortAge(f.lastRaisedAt)}</span>
              </span>
            ))}
            {t && t.carrying.length > 0 && (
              <A onClick={onGoMemory} s="display:inline-flex;align-items:center;min-height:32px;font-size:14px;font-weight:700;color:#0F6B63;text-decoration:none;margin-top:4px;">
                All of it, with provenance →
              </A>
            )}
          </div>

          <div style={st('min-width:0;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
            <span style={st('display:block;font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;padding-bottom:6px;')}>Refused today</span>
            {(t?.refused ?? []).map((r, i) => (
              <span key={i} style={st('display:block;padding:5px 0 6px;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
                <span style={st('display:block;font-size:14px;font-style:italic;color:#54606C;line-height:1.4;text-wrap:pretty;')}>{`“${r.said}”`}</span>
                <span style={st('display:block;font-size:14px;color:#22272E;line-height:1.4;margin-top:3px;text-wrap:pretty;')}>{r.reply}</span>
              </span>
            ))}
            <span style={st('display:block;font-size:14px;color:#54606C;line-height:1.4;margin-top:6px;text-wrap:pretty;')}>
              {refusedNote(t?.refused.length ?? null)}
            </span>
          </div>

          <div style={st('min-width:0;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
            <span style={st('display:block;font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;padding-bottom:5px;')}>Steering in force</span>
            {liveDirectives.map((d) => {
              const left = daysLeft(d.expiresAt);
              return (
                <span key={d.id} style={st('display:block;padding:4px 0;min-width:0;')}>
                  <span style={st('display:block;font-size:14px;font-weight:600;color:#22272E;line-height:1.35;text-wrap:pretty;')}>{d.directive}</span>
                  <span style={st(`display:block;font-size:14px;line-height:1.35;margin-top:1px;color:${left !== null && left <= 14 ? '#4A5866' : '#54606C'};`)}>
                    <span>{d.createdByName ?? '–'}</span>
                    {' · '}
                    <span>{left === null ? 'no expiry set' : `expires in ${left}d`}</span>
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The resident's earlier conversations (restored from companion_view.dart:2108-2255). Shown
 * only after today's transcript was opened with a reason; each thread opened is audited.
 */
function PastThreads({ residentId }: { residentId: string }) {
  const list = useApi((c, signal) => companionPastThreads(c, { params: { id: residentId }, signal }).catch(() => null), [residentId]);
  const [open, setOpen] = useState<string | null>(null);
  const thread = useApi((c, signal) => companionPastThread(c, { params: { id: residentId, cid: open! }, signal }), [residentId, open], !!open);
  const rows = list.data?.conversations ?? [];
  if (!rows.length) return null;
  const day = (v: string | null) => (v ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
  return (
    <div data-testid="companion-past" style={st('display:flex;flex-direction:column;gap:6px;padding-top:10px;margin-top:4px;border-top:1px solid rgba(34,39,46,.1);min-width:0;')}>
      <span style={st('font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;')}>Earlier conversations</span>
      {rows.map((r) => (
        <div key={r.id} style={st('min-width:0;')}>
          <A onClick={() => setOpen(open === r.id ? null : r.id)} s="display:flex;align-items:center;gap:8px;min-height:30px;font-size:16px;color:#22272E;text-decoration:none;">
            <span style={st('font-weight:700;')}>{day(r.startedAt)}</span>
            <span style={st('color:#54606C;')}>{`${r.messageCount} ${r.messageCount === 1 ? 'turn' : 'turns'}`}</span>
            <span style={st('margin-left:auto;color:#0F6B63;font-weight:700;')}>{open === r.id ? 'Close' : 'Read'}</span>
          </A>
          {open === r.id && thread.data && (
            <div style={st('display:flex;flex-direction:column;gap:6px;padding:6px 0 4px;')}>
              {thread.data.messages.map((m, i) => (
                <div key={i} style={st(`display:flex;flex-direction:column;align-items:${m.who === 'companion' ? 'flex-start' : 'flex-end'};min-width:0;`)}>
                  <div style={st(`max-width:78%;padding:8px 12px;font-size:16px;line-height:1.45;text-wrap:pretty;border-radius:${m.who === 'companion' ? '3px 13px 13px 13px;background:rgba(34,39,46,.06);color:#22272E;' : '13px 3px 13px 13px;background:#2F5C97;color:#FCFAF4;'}`)}>
                    {m.text || '–'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The list's label for each resident: the first name the design shows, and where two share
 * it, the room ("Beatrice · Rm 214"), else the last initial ("Beatrice O."), so nobody opens
 * the wrong resident.
 */
export function rosterLabels(rows: { residentId: string; name: string; displayName: string; room?: string | null }[]): Map<string, string> {
  const n = new Map<string, number>();
  for (const r of rows) n.set(r.displayName, (n.get(r.displayName) ?? 0) + 1);
  return new Map(rows.map((r) => {
    if ((n.get(r.displayName) ?? 0) < 2) return [r.residentId, r.displayName];
    if (r.room) return [r.residentId, `${r.displayName} · Rm ${r.room}`];
    const parts = r.name.trim().split(/\s+/);
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return [r.residentId, last ? `${r.displayName} ${last.toUpperCase()}.` : r.displayName];
  }));
}
